import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth } from "../firebase";

import { FiTrash2, FiEye, FiEyeOff, FiEdit2, FiX } from "react-icons/fi";
import { uploadImageToGitHub } from "../utils/githubUpload";

export default function PostsList() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [previewImage, setPreviewImage] = useState(null);
  const [editPost, setEditPost] = useState(null);

  const [editData, setEditData] = useState({
    title: "",
    description: "",
    category: null,
    status: "draft",
    imageUrl: "",
  });

  const [newImageFile, setNewImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const user = auth.currentUser;

  /* ================= LOAD POSTS ================= */
  useEffect(() => {
    const q = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) =>
      setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  /* ================= LOAD CATEGORIES ================= */
  useEffect(() => {
    const q = query(collection(db, "categories"), orderBy("name"));
    return onSnapshot(q, (snap) =>
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  /* ================= CLEANUP PREVIEW URL ================= */
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);


  /* ================= ACTIONS ================= */
  const deletePost = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await deleteDoc(doc(db, "posts", id));
  };

  const togglePublish = async (id, status) => {
    await updateDoc(doc(db, "posts", id), {
      status: status === "published" ? "draft" : "published",
      updatedAt: serverTimestamp(),
    });
  };


  const openEdit = (post) => {
    setEditPost(post);
    setEditData({
      title: post.title || "",
      description: post.description || "",
      status: post.status || "draft",
      imageUrl: post.imageUrl || "",
      category: post.category || null,
    });
    setNewImageFile(null);
    setPreviewUrl(null);
  };

  const saveEdit = async () => {
    if (!editPost) return;

    if (!editData.title.trim() || !editData.description.trim()) {
      alert("Title and description are required");
      return;
    }

    setSaving(true);

    let imageUrl = editData.imageUrl;
    if (newImageFile) {
      imageUrl = await uploadImageToGitHub(newImageFile);
    }


    await updateDoc(doc(db, "posts", editPost.id), {
      ...editData,
      imageUrl,
      updatedAt: new Date(),
      updatedBy: {
        uid: user.uid,
        email: user.email,
      },
    });


    setSaving(false);
    setEditPost(null);
    setPreviewUrl(null);
  };


  return (
    <div className="bg-neutral-900 rounded-xl p-6">

      <h2 className="text-lg font-semibold mb-6">All Posts</h2>

      {/* ================= TABLE ================= */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-800 text-neutral-400">
            <tr>
              <th className="p-3 text-left">Image</th>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Category</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Created By</th>
              <th className="p-3 text-left">Updated By</th>
              <th className="p-3 text-right">Actions</th>

            </tr>
          </thead>

          <tbody>
            {posts.map((p) => (
              <tr
                key={p.id}
                className="border-b border-neutral-800 hover:bg-neutral-800/40"
              >
                <td className="p-3">
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover cursor-pointer"
                      onClick={() => setPreviewImage(p.imageUrl)}
                    />
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </td>

                <td className="p-3 max-w-xs truncate">{p.title}</td>

                <td className="p-3">
                  {p.category?.name ? (
                    <span className="px-3 py-1 text-xs rounded-full bg-neutral-800">
                      {p.category.name}
                    </span>
                  ) : (
                    <span className="text-neutral-500">—</span>
                  )}
                </td>

                <td className="p-3">
                  <span
                    className={`text-xs font-medium ${p.status === "published"
                      ? "text-emerald-400"
                      : "text-rose-400"
                      }`}
                  >
                    {p.status}
                  </span>
                </td>

                <td className="p-3 text-xs text-neutral-400">
                  {p.createdBy?.email || "—"}
                </td>

                <td className="p-3 text-xs text-neutral-400">
                  {p.updatedBy?.email || "—"}
                </td>

                <td className="p-3 flex justify-end gap-4 text-neutral-400">
                  <FiEdit2
                    className="cursor-pointer hover:text-white"
                    onClick={() => openEdit(p)}
                  />

                  <span
                    className="cursor-pointer hover:text-white"
                    onClick={() => togglePublish(p.id, p.status)}
                  >
                    {p.status === "published" ? <FiEyeOff /> : <FiEye />}
                  </span>

                  <FiTrash2
                    className="cursor-pointer hover:text-red-500"
                    onClick={() => deletePost(p.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ================= IMAGE PREVIEW MODAL ================= */}
      {previewImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <FiX
              className="absolute top-3 right-3 text-white cursor-pointer"
              onClick={() => setPreviewImage(null)}
            />
            <img
              src={previewImage}
              alt=""
              className="max-h-[80vh] rounded-xl"
            />
          </div>
        </div>
      )}

      {/* ================= EDIT MODAL ================= */}
      {editPost && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          onClick={() => setEditPost(null)}
        >
          <div
            className="bg-neutral-900 border border-neutral-800 rounded-xl w-full max-w-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Edit Post</h3>

            <input
              className="w-full mb-3 bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-sm"
              placeholder="Title"
              value={editData.title}
              onChange={(e) =>
                setEditData({ ...editData, title: e.target.value })
              }
            />

            <textarea
              rows={4}
              className="w-full mb-3 bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-sm"
              placeholder="Description"
              value={editData.description}
              onChange={(e) =>
                setEditData({ ...editData, description: e.target.value })
              }
            />

            <select
              className="w-full mb-3 bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-sm"
              value={editData.category?.id || ""}
              onChange={(e) => {
                const selected = categories.find(
                  (c) => c.id === e.target.value
                );
                setEditData({
                  ...editData,
                  category: selected
                    ? {
                      id: selected.id,
                      name: selected.name,
                      slug: selected.slug,
                    }
                    : null,
                });
              }}
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            {(previewUrl || editData.imageUrl) && (
              <img
                src={previewUrl || editData.imageUrl}
                alt=""
                className="w-full max-h-48 object-cover rounded-lg mb-3"
              />
            )}

            <input
              type="file"
              accept="image/*"
              className="mb-4 text-sm text-neutral-400"
              onChange={(e) => {
                const f = e.target.files[0];
                if (!f) return;
                setNewImageFile(f);
                setPreviewUrl(URL.createObjectURL(f));
              }}
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setEditPost(null)}
                className="px-4 py-2 border border-neutral-700 rounded-lg text-sm hover:bg-neutral-800"
              >
                Cancel
              </button>

              <button
                onClick={saveEdit}
                disabled={saving}
                className="px-5 py-2 bg-red-500 text-black font-semibold rounded-lg hover:opacity-90 disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
